import { Clock } from 'lucide-react';
import { ColorSwatch } from '@/components/ColorSwatch';
import { EmptyState } from '@/components/EmptyState';
import { SectionTitle } from '@/components/SectionTitle';
import { useTimerActions } from '@/components/timer/TimerActionsProvider';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/cn';
import type { ProjectTotal } from '@/lib/dashboard-model';
import { formatDurationHm } from '@/lib/format';

const PERCENT = 100;

export function WeekByProject({ items, timerRunning }: { items: ProjectTotal[]; timerRunning: boolean }): JSX.Element {
  const actions = useTimerActions();
  const max = items.reduce((acc, i) => Math.max(acc, i.seconds), 0);
  return (
    <section aria-labelledby="week-project-heading">
      <SectionTitle id="week-project-heading" title="This week by project" hint="How this week's tracked time is distributed across your projects." />
      {items.length === 0 ? (
        <EmptyState
          title="No data this week"
          action={
            !timerRunning && (
              <Button onClick={actions.startInstant} disabled={actions.pending}>
                <Clock /> Start timer
              </Button>
            )
          }
        />
      ) : (
        <ul className="card space-y-3 p-3">
          {items.map((item) => (
            <li key={item.key}>
              <div className="mb-1 flex items-center justify-between">
                <div className="flex min-w-0 items-center gap-2">
                  {item.color ? <ColorSwatch hex={item.color} size={8} /> : <span aria-hidden="true" className="h-2 w-2 shrink-0 rounded-full bg-muted-foreground/40" />}
                  <span className="truncate text-sm text-foreground">
                    {item.label}
                    {item.archived && <span className="text-muted-foreground"> (archived)</span>}
                  </span>
                </div>
                <span className="font-mono text-xs tabular-nums text-muted-foreground">{formatDurationHm(item.seconds)}</span>
              </div>
              <div className="h-1.5 overflow-hidden rounded-full bg-muted">
                <div
                  className={cn('h-full rounded-full', !item.color && 'bg-muted-foreground/40')}
                  style={{ width: `${max > 0 ? (item.seconds / max) * PERCENT : 0}%`, ...(item.color ? { backgroundColor: item.color } : {}) }}
                />
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
